import { IconLoaderService } from '@app/core/services/icon-loader.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormlyFieldAnyComponent } from '@app/shared/components/formly-components/formly-field-any/formly-field-any.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormlyModule } from '@ngx-formly/core';
import { IonicModule } from "@ionic/angular";
import { SubSink } from 'subsink';
import { DynamicIconComponent } from "@app/shared/components/dynamic-icon";

@Component({
  selector: 'app-formly-field-boolean',
  templateUrl: './formly-field-boolean.component.html',
  styleUrls: ['./formly-field-boolean.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FormlyModule,
    FormsModule,
    IonicModule,
    DynamicIconComponent
],
})
export class FormlyFieldBooleanComponent extends FormlyFieldAnyComponent  implements OnInit, OnDestroy {
  public value: boolean | null = null;
  // public faTrueMark = faSquareCheck;
  // public faFalseMark = faXmark;
  // public faIndeterminateMark = faQuestion;
  private subsink = new SubSink();

  constructor(private IconLoaderService: IconLoaderService,
              private changeDetectorRef: ChangeDetectorRef) {
    super();
    this.IconLoaderService.loadIcons(['fa-square-check', 'fa-xmark', 'fa-question','checkmark']);
  }
  
  ngOnInit(): void {
    // Subscribe to formControl value changes to update localValue
    this.subsink.sink = this.formControl.valueChanges.subscribe(value => {
      this.updateLocalValue(value);
    });
    
    // Initialize with current value
    this.updateLocalValue(this.formControl.value);
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }
  
  private updateLocalValue(modelValue: boolean | null | undefined): void {
    this.value = modelValue === undefined ? null : modelValue;
  }

  /**
   * Check if the field is disabled
   */
  public isDisabled(): boolean {
    return this.field.props?.['disabled'] || this.field.props?.['readonly'] || this.options.formState.disabled || false;
  }

  /**
   * Check if the field is required
   */
  public isRequired(): boolean {
    return this.field.props?.['required'] || false;
  }

  /**
   * Set the boolean state
   * @param state - true, false, or null
   */
  public setBooleanState(state: boolean | null): void {
    if (this.isDisabled()) {
      return;
    }
    this.value = state;
    this.updateFormControl();
  }

  public onCheckboxClick(): void {
    if (this.options.formState.disabled || this.field.props['disabled']) {
      return;
    }
    if (this.value === true) {
      this.value = false;
    } else if (this.value === false) {
      this.value = this.field.props['3states'] ? null : true;
    } else {
      this.value = true;
    }
    this.updateFormControl();
  }

  public getIconName(): string {
    if (this.value === true) {
      return 'checkbox-outline';
    } else if (this.value === false) {
      return 'close-circle-outline';
    } else {
      return 'remove-circle-outline';
    }
  }

  public getIcon() {
    if (this.value === true) {
      return 'fa-solid fa-square-check';
    } else if (this.value === false) {
      return 'fa-solid fa-xmark';
    } else {
      return 'fa-solid fa-question';
    }
  }

  public getIconClass(): string {
    if (this.value === true) {
      return 'boolean-true success';
    } else if (this.value === false) {
      return 'boolean-false danger';
    } else {
      return 'indeterminate';
    }
  }

  public getDisabledClass(): string {
    return (this.options.formState.disabled || this.field.props['disabled']) ? ' disabled' : '';
  }


  onValueChange(newValue: boolean | undefined | null): void {
    this.value = newValue === undefined ? null : newValue;
    this.updateFormControl();
  }

  getClass(): string {
    let classes = 'boolean-input ';
    if (this.value === true) {
      classes += 'boolean-true';
    } else if (this.value === false) {
      classes += 'boolean-false';
    } else {
      classes += 'indeterminate';
    }
    return classes;
  }

  public getText(): string {
    if (this.value === true) {
      return 'COMMON.TRUE';
    } else if (this.value === false) {
      return 'COMMON.FALSE';
    } else {
      return 'COMMON.INDETERMINATE';
    }
  } 

  getIndeterminate(): boolean {
    return this.value === null || this.value === undefined;
  }

  private updateFormControl(): void {
    
    this.formControl.setValue(this.value);
    this.formControl.markAsDirty();
    
    // Trigger onChange callbacks if defined
    this.onChange(this.value);
    this.changeDetectorRef.markForCheck();
  }
}
